import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useManualQuery, useMutation } from 'graphql-hooks';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Popover,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';

import { NotificationItem } from '../NotificationItem';
import { notificationsOpenAtom, rankDialogAtom } from '../../atoms';
import { Context, useAuth } from '../../context/auth';
import { RemovalDrawer } from '../RemovalDrawer';
import { NOTIFICATIONS_PAGINATION } from '../../graphql/notification/query';
import { NOTIFICATION_SEEN, NOTIFICATIONS_READ } from '../../graphql/notification/mutation';
import { getNotificationTarget } from '../../helpers';
import useUnreadNotifications from '../../hooks/useUnreadNotifications';
import { Notification } from '../../types';
import {
  navCount,
  navIcon,
  navIconActive,
  navItem,
  navItemActive,
  navLabel,
} from '../../theme';
import styles from './style';

const LIMIT = 10;
// The next page is loaded when 70% of the list is scrolled.
const LOAD_MORE_RATIO = 0.7;
const MAX_BADGE = 99;

const NotificationBell: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { count, setCount } = useUnreadNotifications();
  const { user } = useAuth() as Context;
  const setRankDialog = useSetAtom(rankDialogAtom);
  const setNotificationsOpen = useSetAtom(notificationsOpenAtom);
  const [notificationsPagination] = useManualQuery(NOTIFICATIONS_PAGINATION, { skipCache: true });
  const [notificationsRead] = useMutation(NOTIFICATIONS_READ);
  const [notificationSeen] = useMutation(NOTIFICATION_SEEN);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState([] as Notification[]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadAtOpen, setUnreadAtOpen] = useState(0);
  const [removalReportId, setRemovalReportId] = useState('');
  const loadingRef = useRef(false);
  // The popup was reopened meanwhile: the answer of the previous opening is ignored.
  const requestIdRef = useRef(0);

  const getPage = useCallback(async (offset: number) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    loadingRef.current = true;
    setLoading(true);
    const { data } = await notificationsPagination({
      variables: { input: { limit: LIMIT, offset, disableTotalDocs: true } },
    }) as any;
    if (requestId !== requestIdRef.current) {
      return;
    }
    if (data && data.notificationsPagination) {
      const { docs } = data.notificationsPagination;
      setNotifications((prev) => (offset === 0 ? docs : [...prev, ...docs]));
      // disableTotalDocs: the server sends no hasNextPage (null), a full page means there may be more.
      setHasNextPage(docs.length === LIMIT);
    }
    loadingRef.current = false;
    setLoading(false);
  }, [notificationsPagination]);

  // Each opening shows the latest notifications; the unread ones become read
  // (the server pushes the new count, 0, on the websocket).
  // The panel is anchored on the side menu itself: a column next to it, as tall as it.
  const open = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget.closest('nav'));
    setNotificationsOpen(true);
    setUnreadAtOpen(count);
    setNotifications([]);
    getPage(0);
    if (count > 0) {
      setCount(0);
      notificationsRead();
    }
  }, [count, getPage, notificationsRead, setCount, setNotificationsOpen]);

  const close = useCallback(() => {
    setAnchorEl(null);
    setNotificationsOpen(false);
  }, [setNotificationsOpen]);

  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (
      hasNextPage
      && !loadingRef.current
      && scrollTop + clientHeight >= scrollHeight * LOAD_MORE_RATIO
    ) {
      getPage(notifications.length);
    }
  }, [getPage, hasNextPage, notifications.length]);

  const onOpenNotification = useCallback((n: Notification) => {
    if (n.status !== 'seen') {
      notificationSeen({ variables: { id: n.id } });
    }
    const target = getNotificationTarget(n);
    if (!target) {
      return;
    }
    close();
    if (target.kind === 'video') {
      navigate(target.commentId ? `/feed?video=${target.videoId}&comment=${target.commentId}` : `/feed?video=${target.videoId}`);
    } else if (target.kind === 'profile') {
      navigate(`/u/${target.userId}`);
    } else if (target.kind === 'removal') {
      setRemovalReportId(target.reportId);
    } else if (target.kind === 'ranking') {
      navigate('/ranking');
    } else if (target.kind === 'rank') {
      setRankDialog({ id: user.id, username: user.username, rank: user.rank || target.rank });
    } else {
      window.open(target.url, '_blank', 'noopener');
    }
  }, [close, navigate, notificationSeen, setRankDialog, user.id, user.rank, user.username]);

  const onOpenSender = useCallback((n: Notification) => {
    if (n.sender && n.sender.id) {
      close();
      navigate(`/u/${n.sender.id}`);
    }
  }, [close, navigate]);

  const closeRemoval = useCallback(() => {
    setRemovalReportId('');
  }, []);

  const badge = useMemo(() => {
    if (count > MAX_BADGE) {
      return `${MAX_BADGE}+`;
    }
    return String(count);
  }, [count]);

  const isEmpty = !loading && !notifications.length;

  return (
    <>
      <Box component="button" type="button" onClick={open} sx={anchorEl ? navItemActive : navItem}>
        <Box sx={anchorEl ? navIconActive : navIcon}><NotificationsNoneOutlined /></Box>
        <Box component="span" sx={navLabel}>{t('admin.notifs.title')}</Box>
        {count > 0 && <Box component="span" sx={navCount}>{badge}</Box>}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={styles.anchorOrigin}
        transformOrigin={styles.transformOrigin}
        BackdropProps={styles.backdropProps}
        PaperProps={styles.paperProps}
      >
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{t('admin.notifs.title')}</Typography>
          {unreadAtOpen > 0 && (
            <Typography sx={styles.newCount}>{t('admin.notifs.new', { count: unreadAtOpen })}</Typography>
          )}
        </Box>
        <Box sx={styles.list} onScroll={onScroll}>
          {notifications.map((n, index) => (
            <NotificationItem
              key={n.id}
              notification={n}
              // The first ones were unread when the popup was opened.
              isNew={index < unreadAtOpen}
              onOpen={onOpenNotification}
              onOpenSender={onOpenSender}
            />
          ))}
          {isEmpty && <Typography sx={styles.empty}>{t('admin.notifs.empty')}</Typography>}
          {loading && (
            <Box sx={styles.loading}>
              <CircularProgress size={14} sx={styles.spinner} />
              {t('admin.notifs.loading')}
            </Box>
          )}
        </Box>
      </Popover>
      <RemovalDrawer reportId={removalReportId} onClose={closeRemoval} />
    </>
  );
});

export default NotificationBell;
